import React, { useEffect, useRef, useState } from "react";
import ProfileHeader from "./ProfileHeader";
import AccountProfile from "./AccountProfile/AccountProfile";
import AccountDashboard from "./AccountDashboard";
import { useNavigate, useParams } from "react-router-dom";
import { useMainStore } from "@/stores";
import { BreadCrumbs } from "@/shared/components/ui/BreadCrumb/BreadCrumbs";
import CommonTabs from "@/shared/components/ui/Tabs/Tabs";
import { AccountsTabs } from "@/shared/utils/utils";
import {
  useAccountRevenue,
  useFetchCompanyInfo,
} from "@/features/accounts/hooks/useAccounts";
import RelationshipMapping from "./RelationshipMapping";
import ConversationsGate from "./Conversations/ConversationsGate";
import ActionPlan from "./AccountPlan/ActionPlan";
import WhitespaceAudit from "./AccountPlan/WhitespaceAudit";
import ConversationsAdminConsentHeader from "./Conversations/ConversationsAdminConsentHeader";
import Loader from "@/shared/components/ui/Loaders/Loader";
import Deals from "./Deals";
import FollowUpAlertToggle from "./FollowUpAlertToggle";

const ProfileDetailedView = () => {
  const { tab, companyDomain } = useParams();
  const { user } = useMainStore();
  const navigate = useNavigate();
  const { data: companyInfo, isLoading: isLoadingCompanyInfo } = useFetchCompanyInfo({
    userId: user?.id,
    companyName: companyDomain,
  });
  const {
    data: accountRevenue,
    isPending: isRevenuePending,
  } = useAccountRevenue({
    userId: user?.id,
    companyName: companyInfo?.company_name || companyDomain,
    companyDomain: companyInfo?.company_domain,
    enabled: Boolean(user?.id && companyInfo?.company_name),
  });
  const [summaryOpen, setSummaryOpen] = useState(true);
  const contentRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(
    "calc(100dvh - 206px)",
  );

  const handleTabClick = (newTab) => {
    navigate(`/accounts/${encodeURIComponent(companyDomain)}/${newTab}`);
  };

  const HEADER_AND_CHROME_PX = 206;
  const SUMMARY_GAP_PX = 4;

  useEffect(() => {
    if (tab === "conversations") {
      setSummaryOpen(false);
    }
  }, [tab]);

  useEffect(() => {
    if (!summaryOpen) {
      setContainerHeight(`calc(100dvh - ${HEADER_AND_CHROME_PX}px)`);
      return;
    }

    const el = contentRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      setContainerHeight(`calc(100dvh - ${HEADER_AND_CHROME_PX}px)`);
      return;
    }

    const applyHeight = () => {
      const summaryHeight = el.getBoundingClientRect().height;
      setContainerHeight(
        `calc(100dvh - ${HEADER_AND_CHROME_PX + summaryHeight + SUMMARY_GAP_PX}px)`,
      );
    };

    applyHeight();
    const ro = new ResizeObserver(applyHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, [summaryOpen, companyInfo?.profile_summary, companyInfo?.company_name]);

  if (isLoadingCompanyInfo && !companyInfo) {
    return (
      <div className="flex flex-col gap-3">
        <BreadCrumbs
          items={[{ label: "Accounts", href: "/accounts" }, { label: tab }]}
        />
        <div className="flex items-center justify-center h-[calc(100dvh-200px)] w-full">
          <Loader text="Loading account details..." />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 px-4">
      <div className="flex flex-col gap-4">
        <BreadCrumbs
          items={[{ label: "Accounts", href: "/accounts" }, { label: tab }]}
        />
        <ProfileHeader
          profile={companyInfo}
          revenue={accountRevenue}
          contentRef={contentRef}
          summaryOpen={summaryOpen}
          setSummaryOpen={setSummaryOpen}
          headerAction={
            <div className="flex items-center gap-4">
              {tab === "conversations" && <ConversationsAdminConsentHeader />}
              <FollowUpAlertToggle
                companyName={companyInfo?.company_name || companyDomain}
              />
            </div>
          }
        />
      </div>
      <div className="w-full">
        <div color="flex flex-col w-full">
          <CommonTabs
            tab={tab}
            handleTabClick={handleTabClick}
            tabs={AccountsTabs}
          />
          <div
            className={`${tab === "relationship" ? "p-0" : "p-4"} shadow-sm rounded-b-[16px] ${tab === "dashboard"
              ? "rounded-tr-[16px] rounded-tl-none"
              : tab === "deals"
                ? "rounded-tl-[16px] rounded-tr-none"
                : "rounded-t-[16px]"
              } bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 w-full ${tab === "accountPlan" || tab === "conversations"
                ? "flex min-h-0 flex-col overflow-hidden"
                : "overflow-y-auto scrollbar-hide"
              }`}
            style={{ height: containerHeight }}
          >
            {/* Tab content */}

            {tab === "dashboard" && (
              <AccountDashboard
                companyName={companyInfo?.company_name || companyDomain}
                companyIdentifier={companyDomain}
              />
            )}
            {(tab === "account360" || tab === "accountProfile") && (
              <AccountProfile
                profile={companyInfo}
                accountRevenue={accountRevenue}
                isRevenuePending={isRevenuePending}
              />
            )}
            {tab === "accountPlan" && <ActionPlan />}
            {tab === "whitespaceAudit" && <WhitespaceAudit />}
            {tab === "relationship" && (
              <RelationshipMapping height={containerHeight} />
            )}
            {tab === "conversations" && <ConversationsGate />}
            {tab === "deals" && (
              <Deals
                companyIdentifier={companyDomain}
                companyName={companyInfo?.company_name || companyDomain}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileDetailedView;
